import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchProfessionalProfile, INVITE_PROFESSIONALS_TO_JOBS } from '~/actions';
import { clearSuccess } from '~/actions/success';
import { jobKeys, useInviteProfessionalsToJobsMutation } from '~/domain/Jobs';
import {
  factoryGetInvitableOnsiteInterviewsKey,
  IGetInvitableOnsiteInterviewsResult,
  useGetInvitableOnsiteInterviews,
} from '~/domain/PermanentJobs';
import { useAppSelector } from '~/hooks';

export const useInviteToExistingInterviewModal = (
  isOpen: boolean,
  jobPostingId: string,
  professionalId: string,
  officeId: string,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [selectedInterviewIds, setSelectedInterviewIds] = useState<string[]>([]);
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const {
    isPending: isLoadingInvite,
    isSuccess: isSuccessInvite,
    mutate: inviteProfessionalsToJobs,
    reset: resetInviteProfessionalsToJobs,
  } = inviteProfessionalsToJobsMutation;

  const { data: interviews, isLoading } = useGetInvitableOnsiteInterviews(
    { postingId: jobPostingId, professionalId },
    { enabled: isOpen },
  );

  const professional = useAppSelector((state) => state.user.professionalProfile);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProfessionalProfile(professionalId, officeId));
    }
  }, [isOpen, professionalId, officeId, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedInterviewIds([]);
      resetInviteProfessionalsToJobs();
    }
  }, [isOpen, resetInviteProfessionalsToJobs]);

  const handleToggle = (id: string) => {
    // Defensive guard: an interview the candidate is already invited to must never
    // enter the selection, even if the disabled UI in InterviewRow is bypassed.
    const isAlreadyInvited = interviews?.some(
      (interview) => interview.onsiteInterviewId === id && interview.isInvited,
    );

    if (isAlreadyInvited) {
      return;
    }

    setSelectedInterviewIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleInvite = () => {
    inviteProfessionalsToJobs(
      {
        jobIds: selectedInterviewIds,
        professionalIds: [professionalId],
        officeId,
      },
      {
        onSuccess: (_data, vars) => {
          const invitedInterviewIds = new Set(vars.jobIds);

          vars.jobIds.forEach((jobId) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
          });

          queryClient.setQueryData<IGetInvitableOnsiteInterviewsResult>(
            factoryGetInvitableOnsiteInterviewsKey({ postingId: jobPostingId, professionalId }),
            (old) => {
              if (!old) return old;
              return {
                ...old,
                data: old.data.map((interview) =>
                  invitedInterviewIds.has(interview.onsiteInterviewId)
                    ? { ...interview, isInvited: true }
                    : interview,
                ),
              };
            },
          );
          dispatch(clearSuccess(INVITE_PROFESSIONALS_TO_JOBS));
        },
      },
    );
  };

  return {
    interviews,
    isLoading,
    isLoadingInvite,
    isSuccessInvite,
    professional,
    selectedInterviewIds,
    handleInvite,
    handleToggle,
  };
};
